// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import React, { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Action,
    Button,
    ContentLoader,
    Dropdown,
    Heading,
    ListAction,
    Menu,
    MenuSelectableItem,
    Pagination,
    Text,
    Toolbar,
    ToolbarExpander,
    useTranslate,
} from '@plesk/ui-library';
import { useMutation, useQuery } from '@apollo/client/react';
import { useLocalStorage } from 'common/hooks';
import { addToast } from 'helpers/toasts';
import ButtonWithConfirmation from 'components/common/ButtonWithConfirmation';
import { CopilotContext } from '../../CopilotContext';
import COPILOT_CONVERSATION_HISTORY from 'queries/CopilotConversationHistory.graphql';
import SWITCH_ARCHIVE_STATE from 'queries/SwitchArchiveState.graphql';
import DELETE_COPILOT_CONVERSATION from 'queries/DeleteCopilotConversation.graphql';

type Conversation = {
    id: string;
    title: string;
    archived: boolean;
};

type ConversationFilter = 'active' | 'archived' | 'all';

const CONVERSATION_FILTERS: ConversationFilter[] = ['active', 'archived', 'all'];

// undefined means "all"
const ARCHIVED_BY_FILTER: Record<ConversationFilter, boolean | undefined> = {
    active: false,
    archived: true,
    all: undefined,
};

const ITEMS_PER_PAGE = 30;

type ActiveConversationItemProps = {
    conversation: Conversation;
    translate: ReturnType<typeof useTranslate>;
    onOpen: (conversation: Conversation) => void;
    onArchive: (conversation: Conversation) => void;
};

const ActiveConversationItem: React.FC<ActiveConversationItemProps> = ({ conversation, translate, onOpen, onArchive }) => (
    <div className="plesk-copilot-history-item">
        <Action
            onClick={() => onOpen(conversation)}
            className="plesk-copilot-history-item-title"
            outerProps={{ className: 'plesk-copilot-history-item-title-outer' }}
        >
            {conversation.title}
        </Action>
        <Dropdown
            menu={(
                <Menu>
                    <ListAction icon="archive" onClick={() => onArchive(conversation)}>
                        {translate('history-view.archive')}
                    </ListAction>
                </Menu>
            )}
        >
            <Button icon="three-dots-vertical" ghost tooltip={translate('history-view.actions')} tooltipAsLabel />
        </Dropdown>
    </div>
);

type ArchivedConversationItemProps = {
    conversation: Conversation;
    translate: ReturnType<typeof useTranslate>;
    onUnarchive: (conversation: Conversation) => void;
    onDelete: (conversation: Conversation) => void;
};

const ArchivedConversationItem: React.FC<ArchivedConversationItemProps> = ({ conversation, translate, onUnarchive, onDelete }) => (
    <div className="plesk-copilot-history-item">
        <Text truncate>{conversation.title}</Text>
        <Dropdown
            menu={(
                <Menu>
                    <ListAction icon="unarchive" onClick={() => onUnarchive(conversation)}>
                        {translate('history-view.unarchive')}
                    </ListAction>
                    <ListAction icon="recycle" data-action-name="deleteConfirmation">
                        {({ wrap }) => (
                            <ButtonWithConfirmation
                                target={wrap(translate('history-view.delete')) as ReactElement}
                                confirmationText={translate('history-view.confirm-delete')}
                                isCritical
                                onClick={() => onDelete(conversation)}
                            />
                        )}
                    </ListAction>
                </Menu>
            )}
        >
            <Button icon="three-dots-vertical" ghost tooltip={translate('history-view.actions')} tooltipAsLabel />
        </Dropdown>
    </div>
);

type ConversationHistoryProps = {
    onBack: () => void;
};

const ConversationHistory: React.FC<ConversationHistoryProps> = ({ onBack }) => {
    const translate = useTranslate('components.copilot');
    const { chatId, openConversation: openConversationInChat, startNewChat } = useContext(CopilotContext);
    const [storedFilter, setFilter] = useLocalStorage<string>('copilotHistoryFilter', 'active');
    const filter = CONVERSATION_FILTERS.includes(storedFilter as ConversationFilter) ? storedFilter as ConversationFilter : 'active';
    const [page, setPage] = useState(1);

    const { loading: loadingHistory, data, error, refetch } = useQuery(COPILOT_CONVERSATION_HISTORY, {
        variables: {
            input: {
                archived: ARCHIVED_BY_FILTER[filter],
                pagination: { page, itemsPerPage: ITEMS_PER_PAGE },
            },
        },
        fetchPolicy: 'network-only',
    });

    const [switchArchiveStateMutation, { loading: switchingArchiveState }] = useMutation(SWITCH_ARCHIVE_STATE);
    const [deleteConversationMutation, { loading: deleting }] = useMutation(DELETE_COPILOT_CONVERSATION);
    const loading = loadingHistory || switchingArchiveState || deleting;

    const listRef = useRef<HTMLDivElement>(null);
    const hasFocusedFirstItemRef = useRef(false);

    const handleFilterChange = (nextFilter: ConversationFilter) => {
        setFilter(nextFilter);
        setPage(1);
    };

    useEffect(() => {
        if (error) {
            addToast({ intent: 'danger', message: String(error) });
        }
    }, [error]);

    const history = data?.viewer?.copilotConversationHistory;
    const pageCount = history?.pageInfo?.pageCount ?? 0;

    const conversations = useMemo(() => (history?.nodes ?? [])
        .map(conversation => ({ id: conversation.externalId, title: conversation.title ?? '', archived: conversation.archived })),
    [history]);

    useEffect(() => {
        if (loading || hasFocusedFirstItemRef.current) {
            return;
        }
        const firstItem = listRef.current?.querySelector<HTMLElement>('.plesk-copilot-history-item-title');
        if (firstItem) {
            firstItem.focus();
            hasFocusedFirstItemRef.current = true;
        }
    }, [loading, conversations]);

    const openConversation = (conversation: Conversation) => {
        onBack();
        openConversationInChat(conversation.id).catch(() => {});
    };

    const setConversationArchived = async (conversation: Conversation, archived: boolean) => {
        try {
            await switchArchiveStateMutation({
                variables: { input: { chatId: conversation.id, archived } },
            });
            if (archived && conversation.id === chatId) {
                startNewChat();
            }
            await refetch();
        } catch (switchError) {
            addToast({ intent: 'danger', message: String(switchError) });
        }
    };

    const archiveConversation = (conversation: Conversation) => setConversationArchived(conversation, true);
    const unarchiveConversation = (conversation: Conversation) => setConversationArchived(conversation, false);

    const deleteConversation = async (conversation: Conversation) => {
        try {
            await deleteConversationMutation({
                variables: { input: { chatId: conversation.id } },
            });
            await refetch();
        } catch (deleteError) {
            addToast({ intent: 'danger', message: String(deleteError) });
        }
    };

    return (
        <div className="plesk-copilot-history">
            <div className="plesk-copilot-chat-header">
                <Toolbar>
                    <Button icon="arrow-left" ghost tooltip={translate('history-view.back')} tooltipAsLabel onClick={onBack} />
                    <Heading level={2} className="plesk-copilot-history-title">{translate('history')}</Heading>
                    <ToolbarExpander />
                    <Dropdown
                        menu={(
                            <Menu>
                                {CONVERSATION_FILTERS.map(filterOption => (
                                    <MenuSelectableItem
                                        key={filterOption}
                                        selected={filter === filterOption}
                                        onClick={() => handleFilterChange(filterOption)}
                                    >
                                        {translate(`history-view.filter.${filterOption}`)}
                                    </MenuSelectableItem>
                                ))}
                            </Menu>
                        )}
                    >
                        <Button icon="filter" ghost tooltip={translate('filter')} tooltipAsLabel />
                    </Dropdown>
                </Toolbar>
            </div>
            <div className="plesk-copilot-chat-messages">
                {loading && <ContentLoader />}
                {!loading && conversations.length === 0 && (
                    <div className="plesk-copilot-history-empty">
                        <Text intent="muted">{translate('history-view.empty')}</Text>
                    </div>
                )}
                {!loading && conversations.length > 0 && (
                    <div className="plesk-copilot-history-list" ref={listRef}>
                        {conversations.map(conversation => (
                            conversation.archived
                                ? (
                                    <ArchivedConversationItem
                                        key={conversation.id}
                                        conversation={conversation}
                                        translate={translate}
                                        onUnarchive={unarchiveConversation}
                                        onDelete={deleteConversation}
                                    />
                                )
                                : (
                                    <ActiveConversationItem
                                        key={conversation.id}
                                        conversation={conversation}
                                        translate={translate}
                                        onOpen={openConversation}
                                        onArchive={archiveConversation}
                                    />
                                )
                        ))}
                    </div>
                )}
                {!loading && pageCount > 1 && (
                    <Pagination
                        current={page}
                        total={pageCount}
                        onSelect={setPage}
                    />
                )}
            </div>
        </div>
    );
};

export default ConversationHistory;
